import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BookMarked,
  Bot,
  ClipboardCheck,
  Code2,
  ExternalLink,
  FileText,
  Globe,
  Heart,
  MessageSquare,
  Server,
  Sparkles,
  Users,
} from 'lucide-react';
import {
  useMCStats,
  useEvolutionInstances,
  useSocialStats,
  formatNumber,
} from '@/hooks/useIntegrations';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useFavorites } from '@/hooks/useFavorites';
import { useItemsList } from '@/hooks/useItems';
import { useAlerts } from '@/hooks/useAlerts';
import { useAuditoriaStats } from '@/hooks/useAuditoria';
import { ItemStatusBadge, TIPO_LABELS } from '@/components/ItemStatusBadge';
import type { ItemStatus } from '@central/types';

const CATEGORIAS = [
  { key: 'app-proprio', label: 'Apps Próprios', icon: Code2 },
  { key: 'app-aluno', label: 'Apps Alunos', icon: Heart },
  { key: 'agente-autonomo', label: 'Agentes Autônomos', icon: Bot },
  { key: 'agente-loop', label: 'Agentes Loop', icon: Bot },
  { key: 'dominio', label: 'Domínios', icon: Globe },
  { key: 'mentorada', label: 'Mentoradas', icon: Users },
  { key: 'doc', label: 'Docs', icon: FileText },
  { key: 'skill', label: 'Skills', icon: Sparkles },
  { key: 'termo-glossario', label: 'Glossário', icon: BookMarked },
] as const;

type CategoriaStats = Record<string, { total: number; alertas: number }>;

export function DashboardPage(): JSX.Element {
  const { data: stats } = useQuery({
    queryKey: ['items', 'stats'],
    queryFn: () => api<CategoriaStats>('/api/items/stats/categorias'),
  });
  const { data: favorites } = useFavorites();
  const { data: recentData } = useItemsList({ limit: 5 });
  const { data: alertsData } = useAlerts({ unreadOnly: true });
  const { data: auditoriaStats } = useAuditoriaStats();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Visão geral do seu ecossistema</p>
      </div>

      {/* Banner de auditoria pendente */}
      {auditoriaStats && auditoriaStats.pendentes > 0 && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ClipboardCheck className="h-5 w-5 text-primary" />
              <div>
                <div className="font-medium text-sm">
                  {auditoriaStats.pendentes} item{auditoriaStats.pendentes !== 1 ? 's' : ''} aguardando auditoria
                </div>
                <div className="text-xs text-muted-foreground">
                  Progresso: {auditoriaStats.percentual}% · {auditoriaStats.total - auditoriaStats.pendentes}/{auditoriaStats.total} revisados
                </div>
              </div>
            </div>
            <Link to="/auditoria">
              <Button size="sm">Começar auditoria</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Integrações — status rápido */}
      <IntegrationsStrip />

      {/* Barra de contadores */}
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {CATEGORIAS.map((cat) => (
          <Link
            key={cat.key}
            to={`/categoria/${cat.key}`}
            className="flex items-center gap-1.5 hover:text-foreground transition-colors"
          >
            <cat.icon className="h-3.5 w-3.5" />
            <span>
              <span className="font-medium text-foreground">{stats?.[cat.key]?.total ?? 0}</span>{' '}
              {cat.label.toLowerCase()}
            </span>
          </Link>
        ))}
      </div>

      {/* Favoritos */}
      <section>
        <h2 className="text-sm font-medium mb-3 flex items-center gap-2">
          <Heart className="h-4 w-4" /> Favoritos
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {favorites && favorites.items.length > 0 ? (
            favorites.items.map((item) => (
              <Link key={item.id} to={`/items/${item.id}`}>
                <Card className="h-full hover:border-primary/40 transition-colors">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <ItemStatusBadge status={item.status as ItemStatus} compact />
                      <span className="text-[10px] text-muted-foreground truncate">
                        {TIPO_LABELS[item.tipo]}
                      </span>
                    </div>
                    <div className="font-medium text-sm truncate">{item.nome}</div>
                    {item.url && (
                      <div className="text-[10px] text-muted-foreground truncate">
                        {item.url.replace(/^https?:\/\//, '')}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))
          ) : (
            Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="border-dashed">
                <CardContent className="p-4 text-center text-xs text-muted-foreground">
                  Favorite um item
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </section>

      {/* Categorias */}
      <section>
        <h2 className="text-sm font-medium mb-3">Categorias</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {CATEGORIAS.map((cat) => {
            const stat = stats?.[cat.key];
            const total = stat?.total ?? 0;
            const alertas = stat?.alertas ?? 0;
            return (
              <Link key={cat.key} to={`/categoria/${cat.key}`}>
                <Card className="h-full hover:border-primary/40 transition-colors">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between text-base">
                      <span className="flex items-center gap-2">
                        <cat.icon className="h-4 w-4 text-muted-foreground" />
                        {cat.label}
                      </span>
                      {alertas > 0 && (
                        <span className="flex items-center gap-1 text-xs text-destructive">
                          <AlertTriangle className="h-3 w-3" />
                          {alertas}
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-semibold">{total}</div>
                    <div className="text-xs text-muted-foreground">itens</div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Atividade e alertas */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">📥 Adicionado recentemente</CardTitle>
          </CardHeader>
          <CardContent>
            {!recentData || recentData.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhum item ainda. Comece adicionando um com "+ Adicionar" no topo.
              </p>
            ) : (
              <ul className="space-y-2">
                {recentData.items.map((item) => (
                  <li key={item.id} className="flex items-center gap-2 text-sm">
                    <ItemStatusBadge status={item.status as ItemStatus} compact />
                    <Link to={`/items/${item.id}`} className="flex-1 truncate hover:text-primary">
                      {item.nome}
                    </Link>
                    <span className="text-[10px] text-muted-foreground">
                      {TIPO_LABELS[item.tipo]}
                    </span>
                    <ExternalLink className="h-3 w-3 text-muted-foreground" />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">⚠️ Alertas não lidos</CardTitle>
          </CardHeader>
          <CardContent>
            {!alertsData || alertsData.alerts.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum alerta no momento. 🎉</p>
            ) : (
              <ul className="space-y-1.5">
                {alertsData.alerts.slice(0, 5).map((a) => (
                  <li key={a.id} className="text-sm">
                    <div className="font-medium truncate">{a.titulo}</div>
                    {a.descricao && (
                      <div className="text-xs text-muted-foreground truncate">{a.descricao}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

function IntegrationsStrip(): JSX.Element | null {
  const { data: mc } = useMCStats();
  const { data: evo } = useEvolutionInstances();
  const { data: social } = useSocialStats();

  const connectedWa = evo?.instances.filter((i) => i.ultimo_status === 'open').length ?? 0;
  const totalFollowers = (social?.por_plataforma ?? []).reduce(
    (s, a) => s + (a.seguidores ?? 0),
    0,
  );

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Link to="/integracoes" className="block">
        <Card className="transition-colors hover:border-primary/40">
          <CardContent className="flex items-center gap-3 pt-4">
            <Server className="h-5 w-5 text-muted-foreground" />
            <div>
              <div className="text-xs text-muted-foreground">Marketing Command</div>
              <div className="text-sm font-semibold">
                {mc?.ok ?? 0}/{mc?.total ?? 0} agentes OK
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
      <Link to="/integracoes" className="block">
        <Card className="transition-colors hover:border-primary/40">
          <CardContent className="flex items-center gap-3 pt-4">
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            <div>
              <div className="text-xs text-muted-foreground">WhatsApp (Evolution)</div>
              <div className="text-sm font-semibold">
                {connectedWa}/{evo?.instances.length ?? 0} conectadas
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
      <Link to="/integracoes" className="block">
        <Card className="transition-colors hover:border-primary/40">
          <CardContent className="flex items-center gap-3 pt-4">
            <Globe className="h-5 w-5 text-muted-foreground" />
            <div>
              <div className="text-xs text-muted-foreground">Redes sociais</div>
              <div className="text-sm font-semibold">
                {formatNumber(totalFollowers)} seguidores
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
      <Link to="/glossario" className="block">
        <Card className="transition-colors hover:border-primary/40">
          <CardContent className="flex items-center gap-3 pt-4">
            <BookMarked className="h-5 w-5 text-muted-foreground" />
            <div>
              <div className="text-xs text-muted-foreground">Glossário</div>
              <div className="text-sm font-semibold">termos indexados</div>
            </div>
          </CardContent>
        </Card>
      </Link>
    </div>
  );
}
